/**
 * The session plane, as the harness client plugin serves it to Orik's own components.
 *
 * The kernel boots behind the app (`dsh-client-boot.ts`), and Orik's plugin publishes a bridge
 * on the global object. The two used to be separate builds that could not import each other; they
 * are one Vite build now (the mount is a lazy chunk), and the global remains the handshake because
 * the plugin publishes it from inside the kernel's own lifecycle, which is where a test also
 * supplies one. This file is the app's side of that handshake, and it is deliberately thin.
 */
import { bootDshClient } from "./dsh-client-boot";
import type { OrikDshBridge } from "../../../harness/orik-client";

// Shared event name, kept as a literal so the app's entry does not import the lazy harness chunk.
const CATALOGUE_BRIDGE_EVENT = "atlas:dsh-catalogue-ready";


/** The global the plugin publishes. */
const BRIDGE_GLOBAL = "__ORIK_DSH__";

/** How long to wait for a kernel that may be booting behind this call. */
const BRIDGE_WAIT_MS = 20_000;

/**
 * The published bridge, booting the client if it has not booted.
 *
 * @returns the bridge Orik's client plugin published; rejects when the kernel did not come up with one.
 */
export async function dshBridge(): Promise<OrikDshBridge> {
  const bridge = await bridgeOrUndefined();
  if (bridge === undefined) throw new Error("orik web: the session client booted without publishing its bridge");
  return bridge;
}

/**
 * Whether this page can reach a session bridge.
 *
 * A built page is served the harness client artifact and a test supplies the bridge directly, so a
 * *published* bridge is used wherever it is found. Only the wait for a kernel that has not booted yet
 * belongs to production: a dev server has no client to wait for, and sitting on a twenty-second wait
 * there was the whole reason this used to be a build check.
 */
export function sessionBridgeAvailable(): boolean {
  return (globalThis as { __ORIK_DSH__?: unknown }).__ORIK_DSH__ !== undefined || import.meta.env.PROD;
}

/** The parts of a published bridge this reader needs: dsh's own account of the conversations. */
interface CatalogueBridge {
  catalogueReady(): boolean;
  subscribeCatalogue(listener: () => void): () => void;
  list(): {
    readonly id: string;
    readonly title?: string | undefined;
    readonly archived: boolean;
    readonly updatedAt?: number | undefined;
    readonly running?: boolean | undefined;
    readonly blank?: boolean | undefined;
    readonly workspaceId?: string | undefined;
  }[];
}

/** One conversation, as the session plane accounts for it. */
export interface CatalogueSession {
  readonly id: string;
  readonly title?: string | undefined;
  readonly archived: boolean;
  readonly updatedAt?: number | undefined;
  readonly running?: boolean | undefined;
  readonly blank?: boolean | undefined;
  readonly workspaceId?: string | undefined;
}

/** The tenant's conversations, as the session plane lists them. */
export interface Catalogue {
  /** Membership: the ids dsh accounts for this deployment, in dsh's own order. */
  readonly ids: string[];
  readonly summaries: Map<string, CatalogueSession>;
  readonly archived: Set<string>;
  /**
   * Whether the native session and workspace baselines have arrived. Ready and empty is a valid
   * answer; a pending/unreachable catalogue is not.
   */
  readonly available: boolean;
}

/** Project a bridge's conversations into the reader's shape. */
function readCatalogue(bridge: CatalogueBridge): Catalogue {
  const sessions = bridge.list();
  return {
    // Membership is dsh's own: the workspace registry's rows, with subagent children excluded. The bridge
    // decides that (see `nativeSummaries`); this reader only projects what it is handed.
    ids: sessions.map(session => session.id),
    summaries: new Map(sessions.map(session => [session.id, session])),
    archived: new Set(sessions.filter(session => session.archived).map(session => session.id)),
    available: bridge.catalogueReady(),
  };
}

/** Equality for the projection, not the native store's unrelated loading/part state. */
function catalogueKey(catalogue: Catalogue): string {
  return JSON.stringify([catalogue.available, [...catalogue.summaries.values()]]);
}

/**
 * Observe native catalogue changes across the kernel's asynchronous publication.
 * No timer or list RPC: the native controller owns baseline loading and reconnect repair.
 */
export function watchDshCatalogue(listener: (catalogue: Catalogue) => void): () => void {
  let stopped = false;
  let attached: CatalogueBridge | undefined;
  let unsubscribe: (() => void) | undefined;
  let previous: string | undefined;
  const notify = (): void => {
    if (stopped || attached === undefined) return;
    const catalogue = readCatalogue(attached);
    // Native store notifications also carry unrelated projection/loading changes. Only repaint when
    // the sidebar's values or readiness change, not when a baseline request enters/exits loading.
    const key = catalogueKey(catalogue);
    if (key === previous) return;
    previous = key;
    listener(catalogue);
  };
  const attach = (): void => {
    if (stopped) return;
    const bridge = (globalThis as { __ORIK_DSH__?: CatalogueBridge }).__ORIK_DSH__;
    if (bridge === undefined || bridge === attached) return;
    unsubscribe?.();
    attached = bridge;
    unsubscribe = bridge.subscribeCatalogue(notify);
    notify();
  };
  window.addEventListener(CATALOGUE_BRIDGE_EVENT, attach);
  attach();
  void bootDshClient().then(attach).catch(() => undefined);
  return () => {
    stopped = true;
    window.removeEventListener(CATALOGUE_BRIDGE_EVENT, attach);
    unsubscribe?.();
  };
}

/** The published bridge, waiting for the kernel to get there. */
async function bridgeOrUndefined(): Promise<OrikDshBridge | undefined> {
  const published = (): OrikDshBridge | undefined => (globalThis as Record<string, unknown>)[BRIDGE_GLOBAL] as OrikDshBridge | undefined;
  const present = published();
  if (present !== undefined) return present;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // A failed boot rejects here with its reason, so the caller's retry says why rather than
    // waiting out a bridge that cannot arrive.
    await Promise.race([
      bootDshClient(),
      new Promise<void>(resolve => { timer = setTimeout(resolve, BRIDGE_WAIT_MS); }),
    ]);
    const bridge = published();
    if (bridge === undefined) console.warn("orik web: the harness client did not publish its chat bridge");
    return bridge;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
